/*
 * ssc-order-submit.js
 *
 * SSC B2B order portal, theme side. Handoff SS7 of
 * ~/ClaudeCoWork/SSC_ORDER_SUBMIT_HANDOFF.md.
 *
 * Two jobs, one file:
 *
 *   1. Everywhere: capture `?acct=<key>` off the Locksmith magic URL into a
 *      cookie, and mirror it onto the cart as an attribute.
 *   2. Cart page only: replace Checkout with Submit order, posting the cart to
 *      the endpoint, which builds the B2B draft the store actually gets billed
 *      for.
 *
 * The `acct` key identifies a STORE, not a person. It grants nothing except
 * the ability to submit an order billed to that store's card on file - the
 * same thing the magic URL already implies - so a cookie is an appropriate
 * place for it. The endpoint re-validates it on every submit and can revoke it
 * by flipping one row.
 *
 * The query param is deliberately NOT stripped from the URL: staff are told to
 * bookmark that link, and rewriting it out from under them would produce
 * bookmarks that work once.
 *
 * Loaded globally from sections/ssc-header.liquid, which renders on every page
 * via header-group.json. That section also sets `data-ssc-portal` inline so the
 * Checkout button never flashes before this file runs.
 */
(function () {
  'use strict';

  var COOKIE = 'ssc_acct';
  var ATTR = 'ssc_acct';
  var PO_ATTR = 'ssc_po';
  var KEY_RE = /^[A-Za-z0-9_-]{16,64}$/;
  var YEAR = 60 * 60 * 24 * 365;
  var SAVE_DEBOUNCE = 700;

  /*
   * The idempotency key, minted once per cart-page view.
   *
   * The obvious candidate, cart.token, does not work: tested 2026-09-21
   * against the live storefront, the token survives /cart/clear.js and
   * survives re-adding items. Keying on it would make a store's SECOND order
   * of a session collide with the first, and the endpoint would answer with
   * order #1 while the buyer believed they had placed order #2.
   *
   * One id per page view is the right granularity: every press of the button
   * on this page is the same submission (so a double-click is idempotent), and
   * anything that starts a new order starts a new page view.
   */
  var submitId = null;

  function newSubmitId() {
    var bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return Array.prototype.map
      .call(bytes, function (b) { return ('0' + b.toString(16)).slice(-2); })
      .join('');
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function writeCookie(name, value) {
    // Secure is safe to set unconditionally: the storefront is HTTPS-only.
    // Lax, not Strict - staff follow the magic URL in from email and bookmarks.
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + YEAR + '; SameSite=Lax; Secure';
  }

  function acctFromQuery() {
    try {
      return new URLSearchParams(window.location.search).get('acct') || '';
    } catch (e) {
      return '';
    }
  }

  function currentAcct() {
    var fromQuery = acctFromQuery();
    if (KEY_RE.test(fromQuery)) {
      writeCookie(COOKIE, fromQuery);
      return fromQuery;
    }
    var fromCookie = readCookie(COOKIE);
    return KEY_RE.test(fromCookie) ? fromCookie : '';
  }

  function getCart() {
    return fetch(routeRoot() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  function updateCart(body) {
    return fetch(routeRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js ' + res.status);
      return res.json();
    });
  }

  /*
   * Mirror the key onto the cart. The endpoint reads `acct` from the POST body,
   * not from here, so this is not load-bearing for submitting - it exists so
   * the cart page can reveal the panel server-side (no flash), and so an order
   * that somehow reaches native checkout still says which store it came from.
   */
  function syncCartAttribute(acct) {
    return getCart()
      .then(function (cart) {
        if (cart.attributes && cart.attributes[ATTR] === acct) return cart;
        var attributes = {};
        attributes[ATTR] = acct;
        return updateCart({ attributes: attributes });
      })
      .catch(function () {
        /* A cart that won't take an attribute must not break the page. */
      });
  }

  function setCartCount(count) {
    var host = document.getElementById('cart-icon-bubble');
    if (!host) return;

    var bubble = host.querySelector('.cart-count-bubble');
    if (count > 0 && !bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      host.appendChild(bubble);
    }
    if (!bubble) return;

    if (count > 0) {
      bubble.innerHTML = '<span aria-hidden="true">' + (count < 100 ? count : '99+') + '</span>';
    } else {
      bubble.remove();
    }
  }

  // ---------------------------------------------------------------- cart page

  function showStatus(root, kind, title, lines) {
    var box = root.querySelector('[data-ssc-status]');
    if (!box) return;

    box.className = 'ssc-submit-status ssc-submit-status--' + kind;
    box.hidden = false;

    var html = '<p class="ssc-submit-status__title"></p>';
    box.innerHTML = html;
    box.querySelector('.ssc-submit-status__title').textContent = title;

    if (lines && lines.length) {
      var list = document.createElement('ul');
      list.className = 'ssc-submit-status__list';
      lines.forEach(function (line) {
        var li = document.createElement('li');
        li.textContent = line;          // Shopify userErrors, shown verbatim.
        list.appendChild(li);
      });
      box.appendChild(list);
    }
  }

  function fieldValues() {
    var po = document.querySelector('[data-ssc-po]');
    var note = document.querySelector('[data-ssc-note]');
    return {
      po: po ? po.value.trim() : '',
      note: note ? note.value.trim() : ''
    };
  }

  /* Keep PO and notes on the cart so a refresh or a wander back to a product
     page doesn't silently discard what the buyer typed. */
  function wireFieldPersistence() {
    var po = document.querySelector('[data-ssc-po]');
    var note = document.querySelector('[data-ssc-note]');
    var timer = null;

    function save() {
      var values = fieldValues();
      var attributes = {};
      attributes[PO_ATTR] = values.po;
      updateCart({ note: values.note, attributes: attributes }).catch(function () {});
    }

    function queue() {
      window.clearTimeout(timer);
      timer = window.setTimeout(save, SAVE_DEBOUNCE);
    }

    [po, note].forEach(function (el) {
      if (!el) return;
      el.addEventListener('input', queue);
      el.addEventListener('blur', function () {
        window.clearTimeout(timer);
        save();
      });
    });
  }

  function describeFailure(status, data) {
    // userErrors first - quantity rules and catalog complaints land there and
    // are the only messages that tell the buyer what to actually change.
    if (data && Array.isArray(data.errors) && data.errors.length) {
      return { title: 'This order could not be submitted:', lines: data.errors };
    }
    if (data && data.error) {
      return { title: data.error, lines: null };
    }
    if (status === 0) {
      return {
        title: 'Could not reach SSC. Check your connection and try again.',
        lines: null
      };
    }
    return {
      title: 'Something went wrong submitting your order (error ' + status + '). Please contact SSC.',
      lines: null
    };
  }

  function submit(root) {
    var button = root.querySelector('[data-ssc-submit-button]');
    var acct = root.getAttribute('data-acct') || currentAcct();
    var endpoint = root.getAttribute('data-endpoint');

    if (!acct || !endpoint) {
      showStatus(root, 'error', 'This browser is not set up to submit orders. Please use your store link, or contact SSC.', null);
      return;
    }

    var label = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Submitting…';
    }

    var restore = function () {
      if (!button) return;
      button.disabled = false;
      button.textContent = label;
    };

    var status = 0;

    getCart()
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          throw { handled: true, title: 'Your cart is empty.' };
        }

        var values = fieldValues();

        return fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            acct: acct,
            submit_id: submitId,
            cart_token: cart.token,
            po_number: values.po,
            note: values.note,
            items: cart.items.map(function (item) {
              return {
                variant_id: item.variant_id,
                quantity: item.quantity,
                sku: item.sku || ''
              };
            })
          })
        });
      })
      .then(function (res) {
        status = res.status;
        return res.json().catch(function () { return null; });
      })
      .then(function (data) {
        if (status !== 200 || !data || data.ok !== true) {
          var failure = describeFailure(status, data);
          throw { handled: true, title: failure.title, lines: failure.lines };
        }

        // Order exists in Shopify now. Clearing the cart is tidy-up: if it
        // fails, the order still stands, and resubmitting the same cart token
        // returns that same order rather than making a second one.
        // The '{}' is load-bearing: with Content-Type JSON and no body at
        // all, clear.js returns 200 and clears nothing. Verified 2026-09-21.
        return fetch(routeRoot() + 'cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: '{}'
        })
          .catch(function () {})
          .then(function () {
            showSuccess(root, button, label, data.order_name);
          });
      })
      .catch(function (err) {
        restore();
        if (err && err.handled) {
          showStatus(root, 'error', err.title, err.lines);
          return;
        }
        var failure = describeFailure(0, null);
        showStatus(root, 'error', failure.title, failure.lines);
      });
  }

  /*
   * Cart-page sections that describe a cart which no longer exists. Keyed off
   * templates/cart.json; an unrecognised extra section simply stays visible,
   * which is untidy rather than wrong.
   */
  var STALE_ON_SUCCESS = [
    '#shopify-section-bundle_completer',
    '#shopify-section-specials_groups'
  ];

  function hardHide(el) {
    if (el) el.style.setProperty('display', 'none', 'important');
  }

  /*
   * Turn the cart page into the confirmation.
   *
   * Everything here fights Trade's own display rules, so it is a class plus
   * !important rather than the `hidden` attribute. `hidden` is a UA-stylesheet
   * `display: none` and loses to any author rule - the first version used it
   * and the button sat there reading "Submitting..." on top of a successful
   * order, which reads as a failure to the buyer.
   */
  function showSuccess(root, button, label, orderName) {
    document.documentElement.classList.add('ssc-submit-done');
    setCartCount(0);

    STALE_ON_SUCCESS.forEach(function (selector) {
      hardHide(document.querySelector(selector));
    });

    // If the stylesheet somehow didn't load, at least don't leave the button
    // claiming to still be working.
    if (button) {
      button.disabled = true;
      button.textContent = label;
      hardHide(button);
    }

    showStatus(
      root,
      'ok',
      'Order ' + orderName + ' received.',
      ['SSC will confirm your order and add shipping before your card on file is charged.']
    );

    if (root.scrollIntoView) {
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function initCartPage(acct) {
    var root = document.querySelector('[data-ssc-submit]');
    if (!root) return;

    if (!root.getAttribute('data-acct')) {
      root.setAttribute('data-acct', acct);
    }

    submitId = newSubmitId();
    wireFieldPersistence();

    var button = root.querySelector('[data-ssc-submit-button]');
    if (button) {
      button.addEventListener('click', function () {
        submit(root);
      });
    }
  }

  function init() {
    var acct = currentAcct();
    if (!acct) return;

    document.documentElement.setAttribute('data-ssc-portal', '');
    syncCartAttribute(acct);
    initCartPage(acct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
